<template>
  <div class="relative" ref="root">
    <button type="button" class="form-control text-left flex items-center gap-2 min-h-[2.4rem]" :class="{ 'text-theme-37': !selected }" @click="toggle">
      <template v-if="selected">
        <span class="chip chip-muted flex-shrink-0">{{ QUESTIONNAIRE_LABEL[selected.questionnaire] || selected.questionnaire }}</span>
        <span class="truncate"><span class="font-medium">{{ selected.code }}</span> · {{ selected.desc }}</span>
      </template>
      <span v-else>เลือกคำถาม…</span>
      <ChevronDownIcon class="w-4 h-4 ml-auto flex-shrink-0 text-theme-37" />
    </button>
    <div v-if="open" ref="panelEl" class="absolute z-30 mt-1 w-full min-w-[20rem] bg-white border border-gray-200 rounded-md shadow-lg">
      <div class="p-2 border-b border-gray-100 flex gap-2">
        <input ref="searchEl" v-model.trim="search" type="text" class="form-control" placeholder="พิมพ์รหัสหรือคำถาม เช่น HH014, รายได้">
        <select v-model="qn" class="form-select w-36">
          <option value="">ทุกชุด</option>
          <option v-for="k in questionnaires" :key="k" :value="k">{{ QUESTIONNAIRE_LABEL[k] || k }}</option>
        </select>
      </div>
      <div class="max-h-72 overflow-y-auto">
        <div v-if="!results.length" class="px-3 py-4 text-sm text-theme-37 text-center">ไม่พบคำถาม</div>
        <button v-for="q in results" :key="q.code" type="button" class="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 flex items-start gap-2" :class="{ 'opacity-50': !q.mappable }" :disabled="!q.mappable" @click="pick(q)">
          <span class="chip chip-muted flex-shrink-0 mt-0.5">{{ QUESTIONNAIRE_LABEL[q.questionnaire] || q.questionnaire }}</span>
          <span class="min-w-0 text-sm">
            <span class="font-medium text-gray-800">{{ q.code }}</span>
            <span v-if="q.number && q.number !== q.code" class="text-xs text-theme-37"> ({{ q.number }})</span>
            <span class="text-gray-700"> · {{ q.desc }}</span>
            <span class="block text-xs text-theme-37">{{ typeLabel(q) }}<span v-if="!q.mappable"> · ระบบประเมินคำถามประเภทนี้ไม่ได้</span></span>
          </span>
        </button>
        <div v-if="truncated" class="px-3 py-2 text-xs text-theme-37">แสดง 60 รายการแรก พิมพ์เพิ่มเพื่อค้นหาให้แคบลง</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import { QUESTIONNAIRE_LABEL } from "../constants";

const props = defineProps({
  modelValue: { type: String, default: "" },
  questions: { type: Array, default: () => [] },   // normalized questions from the API
  allowedScopes: { type: Array, default: null },   // restrict by engine scope (rule level)
});
const emit = defineEmits(["update:modelValue", "pick"]);

const root = ref(null);
const searchEl = ref(null);
const panelEl = ref(null);
const open = ref(false);
const search = ref("");
const qn = ref("");
const LIMIT = 60;

const byCode = computed(() => Object.fromEntries(props.questions.map((q) => [q.code, q])));
const selected = computed(() => byCode.value[props.modelValue] || null);
const questionnaires = computed(() => [...new Set(props.questions.map((q) => q.questionnaire))]);

const candidates = computed(() => props.questions.filter((q) => {
  if (props.allowedScopes && q.scope && !props.allowedScopes.includes(q.kind === "derived" ? "derived" : q.scope)) return false;
  if (qn.value && q.questionnaire !== qn.value) return false;
  return true;
}));
const matches = computed(() => {
  const s = search.value.toLowerCase();
  if (!s) return candidates.value;
  return candidates.value.filter((q) => `${q.code} ${q.number || ""} ${q.desc}`.toLowerCase().includes(s));
});
const results = computed(() => matches.value.slice(0, LIMIT));
const truncated = computed(() => matches.value.length > LIMIT);

const typeLabel = (q) => {
  const kind = { object_code: "ตัวเลือกเดียว", array: "หลายตัวเลือก", scalar: q.type === "number" ? "ตัวเลข" : "ข้อความ", value_map: "ตัวเลือก", derived: "ค่าคำนวณ", welfare_array: "สวัสดิการที่ได้รับ" }[q.kind];
  return kind || q.type;
};

// inside a modal the list opens below the visible part of the scrolling body: keep it in view (also while it shrinks/grows as the admin types)
const reveal = () => { if (panelEl.value && panelEl.value.scrollIntoView) panelEl.value.scrollIntoView({ block: "nearest" }); };
const toggle = async () => {
  open.value = !open.value;
  if (!open.value) return;
  await nextTick();
  if (searchEl.value) searchEl.value.focus({ preventScroll: true });
  reveal();
};
const pick = (q) => { emit("update:modelValue", q.code); emit("pick", q); open.value = false; search.value = ""; };
const onDoc = (e) => { if (root.value && !root.value.contains(e.target)) open.value = false; };
onMounted(() => document.addEventListener("mousedown", onDoc));
onBeforeUnmount(() => document.removeEventListener("mousedown", onDoc));
watch(() => props.modelValue, () => { open.value = false; });
watch(() => results.value.length, async () => { if (open.value) { await nextTick(); reveal(); } });
</script>
